import { ComponentProps, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Calendar } from '@/components/ui/Calendar.tsx';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/Drawer.tsx';
import CalendarMonthPicker from '@/components/form/_primitives/date/CalendarMonthPicker.tsx';
import CalendarYearPicker from '@/components/form/_primitives/date/CalendarYearPicker.tsx';
import { POLISH_MONTHS } from '@/components/form/_primitives/date/dateInputUtils.ts';

type CalendarView = 'days' | 'months' | 'years';

type MobileDatePickerDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  value?: Date;
  viewMonth: Date;
  onViewMonthChange: (month: Date) => void;
  onSelect: (date: Date | undefined) => void;
  onMonthPick: (monthIndex: number) => void;
  onYearPick: (year: number) => void;
  calendarOptions?: Omit<
    ComponentProps<typeof Calendar>,
    | 'mode'
    | 'selected'
    | 'onSelect'
    | 'month'
    | 'onMonthChange'
    | 'captionLayout'
    | 'components'
  >;
};

/**
 * Kalendarz date pickera w arkuszu od dołu, dla ekranów poniżej `md`.
 * Popover z komórkami po 32 px i zagnieżdżonymi popoverami miesiąca i roku
 * nie nadaje się pod kciuk — tu komórki mają 44 px, a miesiąc i rok
 * wybiera się z kafli, które na chwilę zastępują siatkę dni zamiast
 * otwierać się nad nią.
 *
 * Puste pole prowadzi przez rok, miesiąc i dzień po kolei — dla daty
 * urodzenia siatka bieżącego miesiąca byłaby tylko przeszkodą. Pole z datą
 * otwiera się od razu na dniach, a rok lub miesiąc z kafli wraca do dni.
 */
const MobileDatePickerDrawer = ({
  open,
  onOpenChange,
  title,
  value,
  viewMonth,
  onViewMonthChange,
  onSelect,
  onMonthPick,
  onYearPick,
  calendarOptions,
}: MobileDatePickerDrawerProps) => {
  // `null` — widok jeszcze nie wybrany, liczy się z wartości przy otwarciu.
  const [view, setView] = useState<CalendarView | null>(null);
  const guided = value === undefined;
  const currentView = view ?? (guided ? 'years' : 'days');

  const handleOpenChange = (next: boolean) => {
    if (!next) setView(null);
    onOpenChange(next);
  };

  const monthIdx = viewMonth.getMonth();
  const year = viewMonth.getFullYear();

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => event.preventDefault()}
        // Powrót fokusu do pola otworzyłby arkusz na nowo — pole otwiera go z `onFocus`.
        onCloseAutoFocus={(event) => event.preventDefault()}
        className="rounded-t-[22px] border-t-0 bg-basic-white"
      >
        <DrawerTitle className="px-4 pt-3 text-center text-base font-semibold text-base-text">
          {title ?? 'Wybierz datę'}
        </DrawerTitle>
        <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
          {currentView === 'days' && (
            <Calendar
              {...calendarOptions}
              mode="single"
              captionLayout="label"
              month={viewMonth}
              onMonthChange={onViewMonthChange}
              selected={value}
              onSelect={onSelect}
              className="mx-auto bg-transparent p-0 [--cell-size:--spacing(11)]"
              classNames={{ caption_label: 'text-base' }}
              components={{
                MonthCaption: () => (
                  <div className="relative z-10 flex items-center justify-center gap-1">
                    <CaptionButton
                      label={POLISH_MONTHS[monthIdx]}
                      onClick={() => setView('months')}
                    />
                    <CaptionButton
                      label={String(year)}
                      onClick={() => setView('years')}
                    />
                  </div>
                ),
              }}
            />
          )}
          {currentView === 'months' && (
            <CalendarMonthPicker
              size="touch"
              currentMonth={monthIdx}
              onPick={(idx) => {
                onMonthPick(idx);
                setView('days');
              }}
            />
          )}
          {currentView === 'years' && (
            <CalendarYearPicker
              size="touch"
              currentYear={year}
              onPick={(picked) => {
                onYearPick(picked);
                setView(guided ? 'months' : 'days');
              }}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const CaptionButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-0.5 rounded-md px-3 py-2 text-base font-medium text-neutral-700 hover:bg-neutral-100"
  >
    {label}
    <ChevronDown aria-hidden className="size-4 text-neutral-500" />
  </button>
);

export default MobileDatePickerDrawer;
